import { v4 as uuid } from '@lukeed/uuid';
import { Bookmark, BookMarked, ChevronDown, Ellipsis, History, SquarePen, Trash } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@shadcn/ui/components/ui/button';
import {
    Dropdown,
    DropdownMenuItem,
    DropdownMenuContent,
    DropdownMenuTrigger,
    DropdownMenu,
} from '@shadcn/ui/components/ui/dropdown-menu';
import { ScrollArea } from '@shadcn/ui/components/ui/scroll-area';
import { Skeleton } from '@shadcn/ui/components/ui/skeleton';
import { cn } from '@shadcn/ui/lib/utils';
import { useDeleteThread } from '../hooks/use-memory';
import { StorageThreadType } from '@mastra/core';
import { useAuth } from '../../../providers/authentication-provider';
import { Separator } from '@shadcn/ui/components/ui/separator';
import { useToast } from '@shadcn/ui/hooks/use-toast';
import { AnimatePresence, motion } from 'motion/react';
import { getDateCategory } from '../../../../helpers/date';

export function ThreadHistoryDropdownButton({
    agentId,
    threadId,
    threads,
    isLoading,
    className,
}: {
    agentId: string;
    threadId: string;
    threads?: StorageThreadType[];
    isLoading: boolean;
    className?: string;
}) {
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className={cn('shadow-md', className)}>
                    <History className="w-4 h-4" />
                    <ChevronDown className="w-4 h-4" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="bottom" align="start">
                <ThreadHistoryDropdownContent
                    agentId={agentId}
                    threadId={threadId}
                    threads={threads}
                    isLoading={isLoading}
                />
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
export function ThreadHistoryDropdownContent({
    agentId,
    threadId,
    threads,
    isLoading,
}: {
    agentId: string;
    threadId: string;
    threads?: StorageThreadType[];
    isLoading: boolean;
}) {
    const { deleteThread } = useDeleteThread();
    const navigate = useNavigate();
    const { toast } = useToast();

    const {
        authState: { userId },
    } = useAuth();
    const handleDelete = async (deleteThreadId: string) => {
        await deleteThread({ threadId: deleteThreadId!, resourceid: userId, agentId });
        toast({
            title: 'Thread deleted',
            description: 'Your thread has been deleted',
            duration: 2000,
        });
    };

    if (isLoading) {
        return (
            <div className="p-4 w-full h-full space-y-2">
                <Skeleton className="h-4" />
                <Skeleton className="h-4" />
                <Skeleton className="h-4" />
                <Skeleton className="h-4" />
                <Skeleton className="h-4" />
            </div>
        );
    }

    // Group threads by date category
    const groupThreadsByDate = (threads: StorageThreadType[]): Record<string, StorageThreadType[]> => {
        const sorted = [...threads].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

        const groupedThreads: Record<string, StorageThreadType[]> = {};

        sorted.forEach((thread) => {
            const date = new Date(thread.updatedAt);
            const category = getDateCategory(date);

            if (!groupedThreads[category]) {
                groupedThreads[category] = [];
            }

            groupedThreads[category].push(thread);
        });

        return groupedThreads;
    };

    const groupedThreads = threads?.length ? groupThreadsByDate(threads) : {};

    return (
        <ScrollArea className="pb-4 w-[256px] *:data-radix-scroll-area-viewport:max-h-[80dvh]">
            <AnimatePresence initial={false}>
                {Object.entries(groupedThreads).map(([dateCategory, categoryThreads]) => (
                    <motion.div
                        layout
                        key={dateCategory}
                        initial={false}
                        animate={{ opacity: 1 }}
                        className="flex flex-col gap-0.5 pb-2"
                    >
                        <p className="text-xs font-extrabold text-text-primary pl-2 py-2">{dateCategory}</p>
                        {categoryThreads.map((thread) => {
                            const isActive = thread.id === threadId;
                            return (
                                <motion.div
                                    layout
                                    initial={false}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0 }}
                                    transition={{
                                        layout: { type: 'spring', stiffness: 300, damping: 30 },
                                        opacity: { duration: 0.3, delay: 0.2 },
                                    }}
                                    className={cn(
                                        'flex cursor-pointer w-full px-2 py-1 items-center focus-visible:outline-hidden transition-colors focus-visible:ring-1 focus-visible:ring-mastra-border-4 rounded-xs group text-sm hover:bg-background-brand-primary-subtle/90',
                                        isActive ? 'bg-background-brand-primary-alt' : ''
                                    )}
                                    key={thread.id}
                                >
                                    <Link to={`/new/home/${thread.id}`} className="flex-1">
                                        <p
                                            className={cn(
                                                'text-text-primary group-hover:text-text-primary transition-all text-sm capitalize py-1 text-ellipsis line-clamp-1 break-all',
                                                isActive ? 'text-text-primary' : ''
                                            )}
                                        >
                                            {thread.title}
                                        </p>
                                    </Link>

                                    <Dropdown>
                                        <Dropdown.Trigger asChild>
                                            <Button
                                                variant="ghost"
                                                // size="icon"
                                                className={cn(
                                                    'h-2 w-2 ml-auto opacity-0 group-hover:opacity-100 transition-opacity'
                                                )}
                                            >
                                                <Ellipsis />
                                                <span className="sr-only">More</span>
                                            </Button>
                                        </Dropdown.Trigger>
                                        <Dropdown.Content>
                                            <Dropdown.Item
                                                className="cursor-pointer text-destructive focus:bg-destructive/15 focus:text-destructive dark:text-red-500"
                                                onSelect={() => {
                                                    handleDelete(thread.id).then(() => navigate(`/new/home/${uuid()}`));
                                                }}
                                            >
                                                <Trash />
                                                <span>Delete</span>
                                            </Dropdown.Item>
                                        </Dropdown.Content>
                                    </Dropdown>
                                </motion.div>
                            );
                        })}
                    </motion.div>
                ))}
            </AnimatePresence>
        </ScrollArea>
    );
}
